/**
 * Recommendation Repository - Platform Persistence layer.
 *
 * Public interface for RecommendationSet artifact persistence. All reads and
 * writes go through this module. No caller touches Supabase directly for
 * recommendation set artifacts.
 *
 * Write path:
 *   writeRecommendationSet()
 *     → supabase.rpc("write_recommendation_set_artifact")  [atomic: version + both INSERTs]
 *     → reads RETURNING (version, created_at)
 *     → assembles ArtifactEnvelope from authoritative database values
 *     → constructArtifact(envelope, content) → RecommendationSet
 *     → retries up to MAX_VERSION_RETRIES times on UNIQUE constraint violation
 *
 * Read path:
 *   readRecommendationSetByVersion / readLatestRecommendationSet / readRecommendationLineage
 *     → joined select on pi_artifact_envelopes + recommendation_set_contents
 *     → rowToRecommendationSet() reassembles typed RecommendationSet
 *
 * displayName parameter:
 *   RecommendationContent carries no display label. The caller (orchestration layer)
 *   supplies displayName as explicit artifact metadata stored in the content table.
 *
 * ArtifactRef fields:
 *   solutionConceptRef and implicationAnalysisRef are denormalized into six scalar
 *   columns. On read they are reassembled into ArtifactRef objects.
 *
 * Responsibility boundary:
 *   This module owns: RPC invocation, retry logic, RETURNING assembly,
 *   constructArtifact() call, error translation, public repository interface.
 *   Database layer owns: version arithmetic, timestamp generation, atomicity.
 *   Governance layer owns: artifact identity semantics, type contracts.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArtifactEnvelope, ArtifactRef } from "@/types/platformArtifactTypes";
import type { RecommendationContent, RecommendationSet } from "@/intelligence/strategicPITypes";
import { constructArtifact } from "@/platform/artifactConstructor";
import { logger } from "@/system/logger";
import { toJson } from "@/integrations/supabase/json";

const MAX_VERSION_RETRIES = 3;
const UNIQUE_VIOLATION_CODE = "23505";

// ─── Private helpers ──────────────────────────────────────────────────────────

type RecommendationEnvelopeWithContent = {
  version:    number;
  created_at: string;
  recommendation_set_contents: {
    sc_project_id:               string;
    sc_artifact_type:            string;
    sc_version:                  number;
    ia_project_id:               string;
    ia_artifact_type:            string;
    ia_version:                  number;
    prioritized_recommendations: unknown;
    deferred_items:              unknown;
    risk_mitigations:            unknown;
    immediate_next_steps:        unknown;
    detected_signals:            unknown;
    synthesis_rationale:         string;
    confidence:                  number;
  } | null;
};

function rowToRecommendationSet(
  projectId: string,
  row: RecommendationEnvelopeWithContent,
): RecommendationSet | null {
  const c = row.recommendation_set_contents;
  if (!c) return null;

  const envelope: ArtifactEnvelope = {
    projectId,
    artifactType: "recommendation_set",
    version:      row.version,
    createdAt:    row.created_at,
  };

  const solutionConceptRef: ArtifactRef = {
    projectId:    c.sc_project_id,
    artifactType: c.sc_artifact_type as ArtifactRef["artifactType"],
    version:      c.sc_version,
  };

  const implicationAnalysisRef: ArtifactRef = {
    projectId:    c.ia_project_id,
    artifactType: c.ia_artifact_type as ArtifactRef["artifactType"],
    version:      c.ia_version,
  };

  const content: RecommendationContent = {
    solutionConceptRef,
    implicationAnalysisRef,
    prioritizedRecommendations: c.prioritized_recommendations as RecommendationContent["prioritizedRecommendations"],
    deferredItems:              c.deferred_items              as RecommendationContent["deferredItems"],
    riskMitigations:            c.risk_mitigations            as RecommendationContent["riskMitigations"],
    immediateNextSteps:         c.immediate_next_steps        as RecommendationContent["immediateNextSteps"],
    detectedSignals:            c.detected_signals            as RecommendationContent["detectedSignals"],
    synthesisRationale:         c.synthesis_rationale,
    confidence:                 c.confidence,
  };

  return constructArtifact(envelope, content);
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a new RecommendationSet artifact version atomically.
 *
 * The RPC assigns the next contiguous version via MAX+1 inside the same
 * transaction. On UNIQUE constraint violation (concurrent writers landing on
 * the same version), this function retries up to MAX_VERSION_RETRIES times.
 */
export async function writeRecommendationSet(
  projectId:   string,
  displayName: string,
  content:     RecommendationContent,
): Promise<RecommendationSet> {
  let attempt = 0;

  while (attempt < MAX_VERSION_RETRIES) {
    const { data, error } = await supabase.rpc("write_recommendation_set_artifact", {
      p_project_id:                  projectId,
      p_display_name:                displayName,
      p_sc_project_id:               content.solutionConceptRef.projectId,
      p_sc_artifact_type:            content.solutionConceptRef.artifactType,
      p_sc_version:                  content.solutionConceptRef.version,
      p_ia_project_id:               content.implicationAnalysisRef.projectId,
      p_ia_artifact_type:            content.implicationAnalysisRef.artifactType,
      p_ia_version:                  content.implicationAnalysisRef.version,
      p_prioritized_recommendations: toJson(content.prioritizedRecommendations),
      p_deferred_items:              toJson(content.deferredItems),
      p_risk_mitigations:            toJson(content.riskMitigations),
      p_immediate_next_steps:        toJson(content.immediateNextSteps),
      p_detected_signals:            toJson(content.detectedSignals),
      p_synthesis_rationale:         content.synthesisRationale,
      p_confidence:                  content.confidence,
    });

    if (!error) {
      const row = (data as Array<{ version: number; created_at: string }>)[0];
      const envelope: ArtifactEnvelope = {
        projectId,
        artifactType: "recommendation_set",
        version:      row.version,
        createdAt:    row.created_at,
      };
      return constructArtifact(envelope, content);
    }

    if ((error as { code?: string }).code === UNIQUE_VIOLATION_CODE) {
      attempt++;
      logger.warn("RecommendationSet version conflict, retrying", { projectId, attempt });
      continue;
    }

    logger.error("writeRecommendationSet RPC failed", { projectId, error });
    throw new Error(`writeRecommendationSet failed: ${error.message}`);
  }

  throw new Error(
    `writeRecommendationSet exhausted ${MAX_VERSION_RETRIES} retries for project ${projectId}`,
  );
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Read a specific RecommendationSet version. Returns null if the version does
 * not exist for this project.
 */
export async function readRecommendationSetByVersion(
  projectId: string,
  version:   number,
): Promise<RecommendationSet | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, recommendation_set_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "recommendation_set")
    .eq("version", version)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    logger.error("readRecommendationSetByVersion failed", { projectId, version, error });
    throw new Error(`readRecommendationSetByVersion failed: ${error.message}`);
  }

  return rowToRecommendationSet(projectId, data as unknown as RecommendationEnvelopeWithContent);
}

/**
 * Read the latest RecommendationSet version for a project. Returns null if no
 * recommendation sets exist for the project.
 */
export async function readLatestRecommendationSet(
  projectId: string,
): Promise<RecommendationSet | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, recommendation_set_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "recommendation_set")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error("readLatestRecommendationSet failed", { projectId, error });
    throw new Error(`readLatestRecommendationSet failed: ${error.message}`);
  }

  if (!data) return null;
  return rowToRecommendationSet(projectId, data as unknown as RecommendationEnvelopeWithContent);
}

/**
 * Read all RecommendationSet versions for a project in ascending version order.
 * Returns an empty array if no recommendation sets exist for the project.
 */
export async function readRecommendationLineage(
  projectId: string,
): Promise<RecommendationSet[]> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, recommendation_set_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "recommendation_set")
    .order("version", { ascending: true });

  if (error) {
    logger.error("readRecommendationLineage failed", { projectId, error });
    throw new Error(`readRecommendationLineage failed: ${error.message}`);
  }

  return (data as unknown as RecommendationEnvelopeWithContent[])
    .map(row => rowToRecommendationSet(projectId, row))
    .filter((d): d is RecommendationSet => d !== null);
}
